import type { SignVisibilitySample } from "../scene/types";
import type { WindowDedupe } from "./dedupe";

/**
 * Turns the renderer's visibility samples into `sign_impression` candidates using the initial
 * sign rule (docs/07 section 5): at least ~50% of the sign's projected area visible for one
 * continuous second, in a foreground tab, one candidate per property per 30-minute window.
 * Two sign surfaces (storefront + skyline) of one building count once. The server re-checks
 * plausibility and dedupes again; this is a filtered estimate, never proof of attention.
 */
export interface SignImpressionCandidate {
  propertyId: string;
  signKind: "storefront" | "skyline";
  visibleFraction: number;
  continuousMs: number;
}

export interface SignTrackerOptions {
  minFraction?: number;
  minContinuousMs?: number;
  /** Optional readability gate in CSS pixels (0 = off; docs/01 section 5 is a design target, not a rule). */
  minProjectedPx?: number;
  dedupe: WindowDedupe;
}

interface Tracking {
  since: number;
  kind: "storefront" | "skyline";
  fraction: number;
}

export const MAX_CONTINUOUS_MS = 600_000;
/**
 * The renderer samples every ~500 ms; a longer silence between samples means the page stalled
 * (busy main thread, throttled tab) and nothing was being looked at. Continuity restarts then
 * instead of crediting the stall as visible time.
 */
export const STALL_GAP_MS = 1500;

export class SignTracker {
  private readonly minFraction: number;
  private readonly minContinuousMs: number;
  private readonly minProjectedPx: number;
  private readonly dedupe: WindowDedupe;
  private tracking = new Map<string, Tracking>();
  private lastSampleAt: number | null = null;

  constructor(opts: SignTrackerOptions) {
    this.minFraction = opts.minFraction ?? 0.5;
    this.minContinuousMs = opts.minContinuousMs ?? 1000;
    this.minProjectedPx = opts.minProjectedPx ?? 0;
    this.dedupe = opts.dedupe;
  }

  /** Feed one batch of samples (~2 per second). Returns the candidates that became eligible now. */
  ingest(samples: readonly SignVisibilitySample[], now: number, tabVisible: boolean): SignImpressionCandidate[] {
    const stalled = this.lastSampleAt !== null && now - this.lastSampleAt > STALL_GAP_MS;
    this.lastSampleAt = now;
    if (!tabVisible || stalled) this.tracking.clear();
    if (!tabVisible) return [];
    // Best surface per property: both kinds of one building are one exposure.
    const best = new Map<string, { kind: "storefront" | "skyline"; fraction: number }>();
    for (const s of samples) {
      if (!Number.isFinite(s.visibleFraction) || s.visibleFraction < this.minFraction) continue;
      if (this.minProjectedPx > 0 && s.projectedMinPx < this.minProjectedPx) continue;
      const cur = best.get(s.propertyId);
      if (!cur || s.visibleFraction > cur.fraction) best.set(s.propertyId, { kind: s.signKind, fraction: s.visibleFraction });
    }
    for (const id of this.tracking.keys()) if (!best.has(id)) this.tracking.delete(id);

    const out: SignImpressionCandidate[] = [];
    for (const [propertyId, b] of best) {
      const t = this.tracking.get(propertyId);
      if (!t) {
        this.tracking.set(propertyId, { since: now, kind: b.kind, fraction: b.fraction });
        continue;
      }
      t.kind = b.kind;
      t.fraction = b.fraction;
      const continuousMs = now - t.since;
      if (continuousMs < this.minContinuousMs) continue;
      if (!this.dedupe.allow(propertyId, now)) continue;
      out.push({
        propertyId,
        signKind: b.kind,
        visibleFraction: Math.min(1, Math.max(0, b.fraction)),
        continuousMs: Math.min(MAX_CONTINUOUS_MS, Math.round(continuousMs)),
      });
    }
    return out;
  }

  /** Forget in-progress exposures (tab hidden, room change, scene disposed). */
  reset(): void {
    this.tracking.clear();
    this.lastSampleAt = null;
  }

  get trackedCount(): number {
    return this.tracking.size;
  }
}
